<template>
  <div>
    <button
      ref="buttonEl"
      class="lg:hidden absolute w-full inset-0"
      :aria-label="`${$t('details')} ${groupedQuotes.psp.displayName}`"
      @click="openDetails"
    />
    <Teleport v-if="detailsOpen" to="body">
      <MobileProviderDetails
        :grouped-quotes="groupedQuotes"
        :common-promotion="commonPromotion"
        :mid-market-rate="midMarketRate"
        @close="close"
      />
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { panelOpen } from './analytics/actions'
import type { GroupedQuotes, Promotion } from './types'
import MobileProviderDetails from './mobile-provider-details/mobile-provider-details.vue'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'

const props = defineProps<{
  groupedQuotes: GroupedQuotes
  commonPromotion: Promotion | null
  midMarketRate: number
  isActiveTab: boolean
}>()

const { $track } = useNuxtApp()

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const route = useRoute()
const router = useRouter()

const buttonEl = useTemplateRef('buttonEl')

onMounted(async () => {
  if (!props.isActiveTab || route.query.details !== props.groupedQuotes.psp.slug) {
    return
  }
  // Open the details panel if the button is available, scroll otherwise
  if (buttonEl.value && getComputedStyle(buttonEl.value).display !== 'none') {
    await openDetails()
  } else {
    await router.replace({ query: { ...route.query, details: undefined } })
    buttonEl.value?.parentElement?.parentElement?.scrollIntoView({ behavior: 'smooth' })
  }
})

const detailsOpen = ref(false)

async function openDetails() {
  if (!detailsOpen.value) {
    if (route.query.details !== props.groupedQuotes.psp.slug) {
      await router.push({
        query: { ...route.query, details: props.groupedQuotes.psp.slug },
      })
    }

    detailsOpen.value = true
    document.body.classList.add('overflow-hidden')

    const unregisterGuard = router.beforeEach((_to, _from, next) => {
      detailsOpen.value = false
      document.body.classList.remove('overflow-hidden')
      unregisterGuard()
      next()
    })

    await panelOpen(
      $track,
      'Details Panel',
      comparisonAnalyticsObj?.value,
      sectionAnalyticsObj?.value,
      getResultAnalyticsObj(props.groupedQuotes.psp.slug),
    )
  }
}

function close() {
  router.go(-1)
}
</script>
